import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Tag, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  ticketEditBody,
  ticketEditPlan,
  updateTicket,
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type Ticket,
  type TicketEditForm,
} from './crmApi'

/**
 * Murojaat tavsifini to'g'rilash — `PUT /api/crm/tickets/{id}`.
 *
 * <h2>⚠️ SERVER TO'LIQ ALMASHTIRADI</h2>
 * `TicketRepository.updateDetails` oltala ustunni so'zsiz qayta yozadi,
 * ya'ni so'rovda tushib qolgan maydon bazada JIMGINA tozalanadi. Tana
 * shuning uchun `ticketEditBody` da quriladi va qorovul uni CHAQIRIB
 * o'lchaydi.
 *
 * <h2>⚠️ HOLAT KO'RINADI, LEKIN TAHRIRLANMAYDI</h2>
 * `TicketEdit` (server) da `status` maydoni ATAYLAB yo'q: har o'tish
 * o'z endpointi bilan boradi (`/start`, `/resolve`, `/reopen`,
 * `/close`) va har biri boshqa ish bajaradi — `resolve` `resolvedAt`
 * ni qo'yadi, `close` `closedAt` va sababni, `reopen` ikkalasini
 * tozalaydi. Formadan holat yuborish bu ishlarni ham, holat mashinasi
 * qoidalarini ham chetlab o'tardi.
 *
 * <h2>⚠️ MAVZU (KIM HAQIDA) HAM TAHRIRLANMAYDI</h2>
 * `subscriberId`/`venueId` serverdagi `TicketEdit` da yo'q va bu
 * ataylab: tiketni boshqa abonentga "ko'chirish" uning butun tarixini
 * (aloqalar, SLA hisobi) jimgina boshqa odamga bog'lardi. Maydon
 * formada ham yo'q — yuborib bo'lmaydigan qiymatni ko'rsatish
 * "yubordim, qabul qilinmadi, menga hech kim aytmadi" holatini
 * yaratardi.
 *
 * <h2>⚠️ MUHIMLIK O'ZGARSA — SLA MUDDATI QAYTA HISOBLANADI</h2>
 * `TicketServiceImpl.update` `due_at` ni `created_at` dan qayta
 * hisoblaydi. Bu tozalash EMAS, lekin ko'rinmas ham bo'lmasligi kerak:
 * muhimlikni pasaytirish kechikkan tiketni ro'yxatdan chiqarib
 * yuborishi mumkin.
 */
interface TicketEditModalProps {
  /** `null` — oyna yopiq */
  ticket: Ticket | null
  onCancel: () => void
  onDone: () => void
}

export function TicketEditModal({ ticket, onCancel, onDone }: TicketEditModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<TicketEditForm>()

  const plan = ticket === null ? null : ticketEditPlan(ticket)

  useEffect(() => {
    if (ticket === null) return
    const next = ticketEditPlan(ticket)
    if (next.block === null) form.setFieldsValue(next.values)
  }, [ticket, form])

  const save = useMutation({
    mutationFn: (values: TicketEditForm) => updateTicket(ticket!.id, ticketEditBody(values, ticket!)),
    onSuccess: onDone,
    // ⚠️ `onError` ATAYLAB yo'q — xato OYNANING ICHIDA chiziladi.
  })

  const dismiss = () => {
    save.reset()
    onCancel()
  }

  const subjectOf = ticket?.venueName ?? ticket?.subscriberLogin ?? null

  return (
    <Modal
      open={ticket !== null && plan !== null && plan.block === null}
      title={t('crm.ticketEdit.title')}
      onCancel={dismiss}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {ticket ? (
        <Typography.Paragraph type="secondary">
          {t('crm.ticketEdit.subtitle')} <Tag>{t(`crm.ticketStatus.${ticket.status}`)}</Tag>
          {subjectOf === null ? null : <Tag>{subjectOf}</Tag>}
        </Typography.Paragraph>
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('crm.ticketEdit.lockedTitle')}
        description={t('crm.ticketEdit.lockedHint')}
      />

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<TicketEditForm> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="subject"
          label={t('crm.ticket.subject')}
          rules={[{ required: true, message: t('crm.ticket.subjectRequired') }]}
        >
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="category" label={t('crm.ticket.category')} rules={[{ required: true }]}>
          <Select
            options={TICKET_CATEGORIES.map((c) => ({
              value: c,
              label: t(`crm.ticketCategory.${c}`),
            }))}
          />
        </Form.Item>
        {/*
          ⚠️ SLA ogohlantirishi maydonning YONIDA: server `due_at` ni
          muhimlikdan qayta hisoblaydi va bu qo'shimcha, ko'rinmas
          oqibat. Oyna tepasidagi umumiy matnda u yo'qolib ketardi.
        */}
        <Form.Item
          name="priority"
          label={t('crm.ticket.priority')}
          extra={t('crm.ticketEdit.priorityHint')}
          rules={[{ required: true }]}
        >
          <Select
            options={TICKET_PRIORITIES.map((p) => ({
              value: p,
              label: t(`crm.ticketPriority.${p}`),
            }))}
          />
        </Form.Item>
        <Form.Item name="description" label={t('crm.ticket.description')}>
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
